// src/components/store/sections/product-cards/CapsulePodProductCard.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { Star, ShoppingCart, Check } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

const CAPSULE_THEMES = [
  {
    podBg: "from-violet-600 via-purple-600 to-indigo-700",
    ringBorder: "border-violet-200",
    priceText: "text-violet-700",
    btnBg: "bg-violet-600 hover:bg-violet-700",
  },
  {
    podBg: "from-rose-500 via-pink-600 to-fuchsia-700",
    ringBorder: "border-rose-200",
    priceText: "text-rose-600",
    btnBg: "bg-rose-600 hover:bg-rose-700",
  },
  {
    podBg: "from-emerald-500 via-teal-600 to-cyan-700",
    ringBorder: "border-emerald-200",
    priceText: "text-emerald-700",
    btnBg: "bg-emerald-600 hover:bg-emerald-700",
  },
  {
    podBg: "from-amber-500 via-orange-500 to-red-600",
    ringBorder: "border-amber-200",
    priceText: "text-orange-700",
    btnBg: "bg-orange-600 hover:bg-orange-700",
  },
];

export default function CapsulePodProductCard({
  product,
  index = 0,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    categoryName,
    rating,
    currentPrice,
    added,
    handleAddToCart,
  } = useCardActions(product, subdomain);

  const theme = CAPSULE_THEMES[index % CAPSULE_THEMES.length];

  return (
    <div
      className={`w-full h-full rounded-[999px] p-3.5 bg-white border-4 ${theme.ringBorder} shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center justify-between text-center group select-none min-h-[420px]`}
    >
      {/* Top Circular Pod Stage */}
      <Link
        href={productUrl}
        className={`relative w-full aspect-square max-w-[190px] rounded-full bg-gradient-to-b ${theme.podBg} flex items-center justify-center p-4 shadow-inner overflow-hidden block`}
      >
        <div className="absolute inset-2 rounded-full border border-white/25 border-dashed pointer-events-none" />
        <div className="relative w-32 h-32">
          <Image
            src={productImg}
            alt={productName}
            fill
            unoptimized
            className="object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-[0_14px_18px_rgba(0,0,0,0.4)]"
          />
        </div>
      </Link>

      {/* Center Info */}
      <div className="px-3 space-y-1 my-2 w-full">
        <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 block truncate">
          {categoryName}
        </span>

        <Link href={productUrl} className="block">
          <h3 className="text-xs sm:text-sm font-black text-slate-900 uppercase truncate hover:underline">
            {productName}
          </h3>
        </Link>

        <div className="flex items-center justify-center gap-0.5 text-amber-400">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star key={s} className="w-3 h-3 fill-amber-400" />
          ))}
          <span className="text-[10px] text-slate-400 font-bold ml-1">({rating.toFixed(1)})</span>
        </div>

        <div className={`text-lg sm:text-xl font-black font-mono ${theme.priceText}`}>
          ৳{currentPrice.toLocaleString()}
        </div>
      </div>

      {/* Bottom Pill CTA */}
      <div className="w-full px-3 pb-2">
        <button
          type="button"
          onClick={(e) => handleAddToCart(e)}
          className={`w-full py-2.5 rounded-full ${theme.btnBg} text-white font-black text-[10px] uppercase tracking-wider shadow-md active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-1.5`}
        >
          {added ? <Check className="w-3.5 h-3.5" /> : <ShoppingCart className="w-3.5 h-3.5" />}
          <span>{added ? "ADDED" : "ADD TO POD"}</span>
        </button>
      </div>
    </div>
  );
}
